/**
 * Geste humain d'ouverture : décider du brouillon AVANT de permettre un montage.
 *
 * `AgentV3View` lance déjà la restauration au montage, mais un avocat peut
 * cliquer pendant son GET. Le geste synchrone historique montait alors un
 * document vierge ; la réponse tardive voyait ce montage, refusait le brouillon
 * durable et la première frappe pouvait supprimer celui-ci au prochain save.
 * Cette façade rejoint la même recherche en vol, puis seulement prend le canvas.
 */

import {
  preparerLeBrouillonAvantOuvertureHumaine,
  type ResultatDIntention,
} from './canvasDocumentOrchestration';
import {
  openCanvas,
  ouvrirLeCanvasPourLAvocat,
  type CanvasOpenOutcome,
} from './canvasOpening';
import { estUnFilConfirme } from './canvasConversationGate';
import { proposerLeChoixDeDepart } from './canvasChoixDeDepart';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';

interface DependancesDOuvertureHumaine {
  readonly filConfirme: (conversationId: string | null) => conversationId is string;
  readonly restaurer: (conversationId: string) => Promise<ResultatDIntention>;
  readonly ouvrir: (conversationId: string | null) => CanvasOpenOutcome | null;
  /** Déploie uniquement l'emplacement de refus, sans réserver ni monter un document. */
  readonly montrerLeRefus: (conversationId: string) => void;
  /** Ce fil a-t-il déjà un document dans l'onglet, monté ou refroidi ? Lu AVANT la recherche. */
  readonly aDejaUnDocument: (conversationId: string) => boolean;
  /** Fait poser le voile de choix du papier à en-tête sur la page neuve qui va monter. */
  readonly proposerLeChoixDeDepart: (conversationId: string) => void;
}

const dependancesParDefaut: DependancesDOuvertureHumaine = {
  filConfirme: estUnFilConfirme,
  restaurer: preparerLeBrouillonAvantOuvertureHumaine,
  ouvrir: ouvrirLeCanvasPourLAvocat,
  montrerLeRefus: (conversationId) => {
    // La recherche a déjà publié son verdict. Déployer sans acquérir la
    // conversation laisse CanvasResidence sans cible logique : aucune page
    // blanche ne peut donc apparaître, tandis que l'écran peut rendre la carte
    // qui explique l'échec à l'avocat.
    openCanvas(`refus de restauration demandé par l'avocat (conversationId=${conversationId})`);
  },
  aDejaUnDocument: (conversationId) => canvasDocumentRuntime.getScope(conversationId) !== null
    || canvasDocumentRuntime.getDraftController(conversationId) !== null,
  proposerLeChoixDeDepart,
};

/**
 * Les frontières de production, exportées pour l'appelant qui doit en remplacer UNE
 * (`canvasOuvertureDepuisUnDossier` : il ouvre le canvas pour y poser une pièce, pas une page neuve).
 */
export const DEPENDANCES_DE_L_OUVERTURE_HUMAINE: DependancesDOuvertureHumaine = dependancesParDefaut;

/**
 * Les trois verdicts après lesquels monter un document est sûr : le fil n'a pas
 * de brouillon, on vient d'y poser le sien, ou il y était déjà.
 *
 * Exportée parce que `CanvasResidence` prend la MÊME décision pour le premier
 * montage d'une session froide (#1592) : deux copies de cette liste dériveraient,
 * et une divergence ici se paie en brouillon d'avocat supprimé.
 */
export const ISSUES_QUI_AUTORISENT_LE_MONTAGE: ReadonlySet<ResultatDIntention['issue']> = new Set([
  'aucun-brouillon',
  'brouillon-pose',
  'brouillon-deja-en-place',
]);

/**
 * Ouvre le canvas uniquement après une décision documentaire sûre.
 *
 * `null` signifie que le geste a été refusé sans rien monter : fil non
 * confirmé/supprimé (garde basse), recherche illisible, ou ouverture
 * concurrente dont le document ne doit pas être recouvert.
 */
export async function preparerEtOuvrirLeCanvasPourLAvocat(
  conversationId: string | null,
  deps: DependancesDOuvertureHumaine = dependancesParDefaut,
): Promise<CanvasOpenOutcome | null> {
  // Rejoue la garde AVANT le réseau : un UUID local n'autorise ni brouillon,
  // ni canvas. La garde basse est conservée dans `ouvrir` contre les courses.
  if (!deps.filConfirme(conversationId)) return deps.ouvrir(conversationId);
  // « Aucun brouillon » ne dit pas « page neuve » : le serveur n'a pas non plus de brouillon pour
  // un document jamais enregistré que le fil tient déjà dans l'onglet (page vierge masquée puis
  // rouverte, document qu'Hector vient d'écrire). Lu avant l'attente, que ce geste peut seul changer.
  const filSansDocument = !deps.aDejaUnDocument(conversationId);
  const restauration = await deps.restaurer(conversationId);
  if (!ISSUES_QUI_AUTORISENT_LE_MONTAGE.has(restauration.issue)) {
    if (restauration.issue === 'restauration-indisponible') {
      deps.montrerLeRefus(conversationId);
    }
    return null;
  }
  const ouverture = deps.ouvrir(conversationId);
  // L'avocat obtient une page neuve : lui proposer son papier à en-tête. Le document monte sans
  // attendre ce choix, sur le défaut du cabinet.
  if (ouverture !== null && filSansDocument && restauration.issue === 'aucun-brouillon') {
    deps.proposerLeChoixDeDepart(conversationId);
  }
  return ouverture;
}
